import { useParams } from "react-router-dom";
import { ArrowLeft, CheckCheck, Download, HelpCircle, Pause, Play, RefreshCw, Send, X } from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/app-layout";
import { LoadMore } from "@/components/load-more";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { StatusBadge } from "@/components/status-badge";
import { TaskTiming } from "@/components/task-timing";
import { spansMultipleDays } from "@/lib/task-timing";
import { useRunAction, useRunProgress, useRunTasks, useTaskAction } from "@/lib/runs";

function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
    return (
        <div className="rounded-lg border border-line px-3 py-2">
            <p className="text-xs text-ink-muted">{label}</p>
            <p className={`text-lg font-semibold ${tone ?? "text-ink"}`}>{value}</p>
        </div>
    );
}

export function RunDetailPage() {
    const { runId } = useParams<{ runId: string }>();
    const run = useRunProgress(runId);
    const taskQuery = useRunTasks(runId ?? "");
    const tasks = taskQuery.items;
    const runAction = useRunAction();
    const taskAction = useTaskAction();

    const uncertain = tasks?.filter((task) => task.status === "UNCERTAIN") ?? [];
    const isActive = run?.status === "RUNNING" || run?.status === "PENDING";
    // Chỉ hiện thêm ngày khi lượt gửi thật sự vắt qua nhiều ngày, đỡ rối mắt
    const multiDay = spansMultipleDays(tasks.map((task) => task.finishedAt));

    return (
        <>
            <PageHeader
                title={run?.campaignName ?? "Chi tiết lượt gửi"}
                description={run ? `${run.accountName} · ${run.triggeredBy === "SCHEDULE" ? "Theo lịch" : "Gửi tay"}` : undefined}
                action={
                    <Link to="/history">
                        <Button variant="ghost" size="sm">
                            <ArrowLeft className="size-4" />
                            Lịch sử
                        </Button>
                    </Link>
                }
            />

            <div className="space-y-4 p-8">
                {/* Không gán sẵn nhãn "hàng đợi hỏng": phần lớn lý do là đang chờ tới
                    giờ chứ không phải sự cố. Câu lý do tự nó đã đủ nghĩa. */}
                {run?.blockedReason && (
                    <Alert tone="warning">
                        <span className="font-medium">Chưa gửi tiếp</span> — {run.blockedReason}
                    </Alert>
                )}

                {run && (
                    <Card>
                        <CardHeader
                            title={<span className="flex items-center gap-2">Tiến độ <StatusBadge status={run.status} /></span>}
                            action={
                                <div className="flex gap-1.5">
                                    {isActive && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => runAction.mutate({ runId: run.id, action: "pause" })}
                                        >
                                            <Pause className="size-4" />
                                            Tạm dừng
                                        </Button>
                                    )}
                                    {run.status === "PAUSED" && (
                                        <Button
                                            size="sm"
                                            onClick={() => runAction.mutate({ runId: run.id, action: "resume" })}
                                        >
                                            <Play className="size-4" />
                                            Tiếp tục
                                        </Button>
                                    )}
                                    {(isActive || run.status === "PAUSED") && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() =>
                                                confirm("Huỷ lượt gửi này? Các nhóm chưa gửi sẽ bị bỏ qua.") &&
                                                runAction.mutate({ runId: run.id, action: "cancel" })
                                            }
                                        >
                                            <X className="size-4" />
                                            Huỷ
                                        </Button>
                                    )}
                                    {run.failedCount > 0 && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            loading={runAction.isPending}
                                            onClick={() => runAction.mutate({ runId: run.id, action: "retry-failed" })}
                                        >
                                            <RefreshCw className="size-4" />
                                            Gửi lại {run.failedCount} lỗi
                                        </Button>
                                    )}
                                    <a href={`/api/runs/${run.id}/export.csv`} download>
                                        <Button variant="secondary" size="sm">
                                            <Download className="size-4" />
                                            CSV
                                        </Button>
                                    </a>
                                </div>
                            }
                        />
                        <CardBody className="grid grid-cols-2 gap-3 md:grid-cols-5">
                            <Stat label="Tổng nhóm" value={run.totalCount} />
                            <Stat label="Đã gửi" value={run.sentCount} tone="text-success" />
                            <Stat label="Lỗi" value={run.failedCount} tone="text-danger" />
                            <Stat label="Chưa rõ" value={run.uncertainCount} tone="text-warning" />
                            <Stat label="Bỏ qua" value={run.skippedCount} />
                        </CardBody>
                    </Card>
                )}

                {uncertain.length > 0 && (
                    <Card>
                        <CardHeader
                            title={
                                <span className="flex items-center gap-2">
                                    <HelpCircle className="size-4 text-warning" />
                                    Cần bạn kiểm tra ({uncertain.length})
                                </span>
                            }
                            description="Những nhóm này chưa xác định được tin đã tới hay chưa. Hệ thống cố ý KHÔNG tự gửi lại để tránh gửi trùng."
                        />
                        <CardBody className="space-y-2">
                            <Alert tone="warning">
                                Mở Zalo kiểm tra nhóm. Nếu tin đã tới, bấm “Đã nhận được”. Nếu chưa, bấm “Gửi lại”.
                            </Alert>
                            {uncertain.map((task) => (
                                <div
                                    key={task.id}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-3 py-2"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-sm text-ink">{task.groupNameSnapshot}</p>
                                        {task.errorMessage && (
                                            <p className="truncate text-xs text-ink-muted">{task.errorMessage}</p>
                                        )}
                                    </div>
                                    <div className="flex shrink-0 gap-1.5">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => taskAction.mutate({ taskId: task.id, action: "verify" })}
                                        >
                                            <RefreshCw className="size-4" />
                                            Kiểm tra lại
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => taskAction.mutate({ taskId: task.id, action: "mark-sent" })}
                                        >
                                            <CheckCheck className="size-4" />
                                            Đã nhận được
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => taskAction.mutate({ taskId: task.id, action: "resend" })}
                                        >
                                            <Send className="size-4" />
                                            Gửi lại
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </CardBody>
                    </Card>
                )}

                <Card>
                    <CardHeader title={`Từng nhóm (${taskQuery.total})`} description="Mỗi dòng là một bản ghi gửi độc lập" />
                    <CardBody className="space-y-1">
                        {tasks.map((task, index) => (
                            <div
                                key={task.id}
                                className="flex items-center gap-3 rounded-lg px-2 py-2 text-sm hover:bg-surface-muted"
                            >
                                <StatusBadge status={task.status} />
                                <span className="min-w-0 flex-1 truncate text-ink">{task.groupNameSnapshot}</span>

                                <TaskTiming
                                    task={task}
                                    previousFinishedAt={tasks[index - 1]?.finishedAt ?? null}
                                    showDay={multiDay}
                                />

                                {task.zaloMsgId && (
                                    <span className="hidden shrink-0 font-mono text-xs text-ink-muted lg:inline">
                                        #{task.zaloMsgId}
                                    </span>
                                )}
                                {task.errorMessage && (
                                    <span className="hidden max-w-64 shrink-0 truncate text-xs text-danger md:inline">
                                        {task.errorMessage}
                                    </span>
                                )}
                                {task.attempt > 1 && (
                                    <span className="shrink-0 text-xs text-ink-muted">thử {task.attempt} lần</span>
                                )}
                            </div>
                        ))}

                        <LoadMore
                            shown={tasks.length}
                            total={taskQuery.total}
                            hasMore={taskQuery.hasMore}
                            loading={taskQuery.isLoadingMore}
                            onLoadMore={taskQuery.loadMore}
                            unit="nhóm"
                        />
                    </CardBody>
                </Card>
            </div>
        </>
    );
}
